import React, { useState, useRef, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Github, Plus, Minus } from 'lucide-react';
import { Tilt } from './Tilt';
import { ScrollCue } from './ScrollCue';
import { ScrollReveal } from './ScrollReveal';
import { SpotlightTitle } from './SpotlightTitle';

const caseStudies = [
  {
    id: 4,
    // tag: 'Developer Tools · Full-Stack',
    title: 'Dispatch',
    subtitle: 'Email API for Developers',
    description:
      'Dispatch is a developer email API that sends real mail through AWS SES. Verify a domain, create an API key, and send with one HTTP request, while delivery, bounce, and complaint events come back as signed webhooks. A queued, idempotent pipeline keeps the API fast and retries safely when sends fail',
    tools: ['Figma', 'Next.js', 'TypeScript', 'Tailwind CSS', 'Hono', 'PostgreSQL', 'Drizzle', 'Supabase', 'Upstash Redis', 'Inngest', 'AWS SES', 'Terraform', 'Vercel', 'Playwright', 'Claude'],
    liveUrl: 'https://dispatchit.ca',
    gitUrl: 'https://github.com/markbuckle/dispatch',
    image: null,
  },
  {
    id: 0,
    // tag: 'Product Design · Full-Stack',
    title: 'ClauseKit',
    subtitle: 'AI Legal Contract Review Assistant',
    description:
      "ClauseKit is a full-stack AI legal assistant built in both the browser and a Microsoft Word add-in. Lawyers can ask questions about the contract they're reviewing and receive risk flags, suggested redlines, analysis, and simulated negotiations",
    tools: ['Claude Design', 'Figma', 'React', 'TypeScript', 'Fluent UI', 'Node.js', 'Express','tRPC','Office.js','MongoDB','Dockerfile','Anthropic SDK', 'GCP'],
    liveUrl: 'https://clause-kit.vercel.app/',
    gitUrl: 'https://github.com/markbuckle/clausekit',
    image: null,
  },
  {
    id: 1,
    // tag: 'Product Design · Full-Stack',
    title: 'LifeSync',
    subtitle: 'Text-to-Calendar Management Application',
    description:
      'A calendar management platform that makes booking appointments, events, and meetings easier than ever. Powered by an AI assistant that helps users stay organized like a personal CEO assistant. Shipped as a web and IOS application',
    tools: ['Figma', 'React', 'TypeScript', 'Tailwind CSS', 'FastAPI', 'GraphQL', 'PostgreSQL', 'Docker', 'Vercel'],
    liveUrl: 'https://thelifesync.vercel.app/',
    gitUrl: 'https://github.com/markbuckle/lifesync',
    image: null,
  },
  {
    id: 2,
    // tag: 'Product Design · Full-Stack',
    title: 'HealthLync',
    subtitle: 'Personalized Health Tracker',
    description:
      'Comprehensive health tracking application that enables users to make sense of complex medical information through intelligent data processing, OCR extraction, and AI-powered insights. Features include interactive dashboards, a RAG chatbot for medical information retrieval, and a guided onboarding tutorial system',
    tools: ['Figma', 'Node.js', 'Express', 'Python', 'MongoDB', 'Plotly.js', 'OpenAI', 'Anthropic Claude', 'Docker'],
    liveUrl: 'https://healthlync.vercel.app/',
    gitUrl: 'https://github.com/markbuckle/health-tracker',
    image: null,
  },
  {
    id: 3,
    // tag: 'SaaS · Full-Stack',
    title: 'FeedFlo',
    subtitle: 'User Feedback Management Web Application',
    description:
      'A SaaS platform for collecting, organizing, and acting on product feedback. Built with authentication, subscription billing, and a clean dashboard that helps teams triage and prioritize user insights at scale',
    tools: ['React', 'Next.js', 'TypeScript', 'Shadcn UI', 'Clerk', 'Supabase', 'PostgreSQL', 'Stripe', 'Vercel'],
    liveUrl: 'https://saasdashboard.vercel.app/',
    gitUrl: 'https://github.com/markbuckle/saas-app',
    image: null,
  },
];

const gradients = [
  'linear-gradient(90deg, #0a0a0a 0%, #353535 50%, #0a0a0a 100%)',
  'linear-gradient(90deg, #111111 0%, #383838 50%, #111111 100%)',
  'linear-gradient(90deg, #0f0f0f 0%, #363636 50%, #0f0f0f 100%)',
];

const CaseStudyCard = ({ study, index }) => {
  const [toolsOpen, setToolsOpen] = useState(false);
  const [rotation, setRotation] = useState(0);

  const handleToggle = () => {
    const next = !toolsOpen;
    setRotation(r => next ? r + 720 : r - 720);
    setToolsOpen(next);
  };

  /* The reveal is scroll-linked rather than a tween on mount because this sits
     inside an AnimatePresence keyed on the active study: a fixed reveal
     replayed its whole 0.5s slide on every tab switch, underneath the
     crossfade already running. Tied to position, it is simply already
     resolved when the card swaps. */
  return (
    <ScrollReveal className="case-study-reveal" distance={44}>
    <Tilt className="case-study-card" max={2.5}>
      {study.image ? (
        <img src={study.image} alt={study.title} className="case-study-image" />
      ) : (
        <div
          className="case-study-image"
          style={{
            background: gradients[index % gradients.length],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SpotlightTitle className="case-study-image-title" text={study.title} />
        </div>
      )}

      <div className="case-study-content">
        <p className="case-study-tag">{study.tag}</p>
        <h3 className="case-study-title white-gradient-text">{study.subtitle}</h3>
        <p className="case-study-description">{study.description}</p>

        <div className="case-study-tools">
          <button
            className="tools-toggle"
            onClick={handleToggle}
            aria-expanded={toolsOpen}
          >
            <span className="tools-label">Primary tools used</span>
            <motion.span
              className="tools-chevron"
              animate={{ rotate: rotation }}
              transition={{ duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              {toolsOpen ? <Minus size={12} strokeWidth={2.5} /> : <Plus size={12} strokeWidth={2.5} />}
            </motion.span>
          </button>

          <AnimatePresence initial={false}>
            {toolsOpen && (
              <motion.div
                className="case-study-tools-tags"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                style={{ overflow: 'hidden' }}
              >
                {study.tools.map((tool) => (
                  <span key={tool} className="tool-tag">{tool}</span>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="case-study-links">
          {study.liveUrl && (
            <a href={study.liveUrl} target="_blank" rel="noopener noreferrer" className="case-study-link case-study-link-ghost">
              <ExternalLink size={13} /> Live
            </a>
          )}
          {study.gitUrl && (
            <a href={study.gitUrl} target="_blank" rel="noopener noreferrer" className="case-study-link case-study-link-ghost">
              <Github size={13} /> Code
            </a>
          )}
        </div>
      </div>
    </Tilt>
    </ScrollReveal>
  );
};

export const DesignWork = () => {
  const [activeId, setActiveId] = useState(caseStudies[0].id);
  const [indicator, setIndicator] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const tabRefs = useRef({});
  const trackRef = useRef(null);

  useLayoutEffect(() => {
    const measure = () => {
      const btn = tabRefs.current[activeId];
      const track = trackRef.current;
      if (!btn || !track) return;
      const bRect = btn.getBoundingClientRect();
      const tRect = track.getBoundingClientRect();
      setIndicator({
        x: bRect.left - tRect.left,
        y: bRect.top - tRect.top,
        w: bRect.width,
        h: bRect.height,
      });
    };
    measure();
    // Web fonts often finish loading after this first measure, which reflows
    // button text width and leaves the pill sized to the fallback font.
    document.fonts?.ready.then(measure);
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [activeId]);

  const activeIndex = caseStudies.findIndex((s) => s.id === activeId);
  const activeStudy = caseStudies[activeIndex];

  return (
    <div className="section-container">
      <ScrollReveal as="p" className="section-label">Projects</ScrollReveal>
      <ScrollReveal as="h2" className="section-title" delay={0.08}><span className="white-gradient-text">My </span><span style={{ background: 'linear-gradient(180deg, #00e5a0 0%, #00b87a 55%, #007a52 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>work</span></ScrollReveal>
      <ScrollReveal as="p" className="section-subtitle" delay={0.14}>
        Here are a few of my end-to-end projects spanning small scale user research, architecture, and design systems. These are passion side projects so there's always more I would like to do with them... just never enough free time
      </ScrollReveal>

      <div className="skills-toggle-wrapper">
        <ScrollReveal className="skills-toggle-outer" delay={0.18}>
          <div className="skills-toggle-track skills-toggle-track--projects" ref={trackRef} role="tablist">
            <svg
              className="skills-toggle-svg"
              width="100%"
              height="100%"
              role="presentation"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="projects-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#ffffff', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#b0b0b0', stopOpacity: 1 }} />
                </linearGradient>
                <linearGradient id="projects-fill" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#585858" />
                  <stop offset="45%" stopColor="#2f2f2f" />
                  <stop offset="100%" stopColor="#181818" />
                </linearGradient>
              </defs>
              <rect
                x="0"
                y="0"
                rx={indicator.h / 2}
                ry={indicator.h / 2}
                stroke="url(#projects-grad)"
                strokeWidth="0.75"
                fill="url(#projects-fill)"
                style={{
                  width: `${indicator.w}px`,
                  height: `${indicator.h}px`,
                  transform: `translate(${indicator.x}px, ${indicator.y}px)`,
                  transition: 'width 0.28s cubic-bezier(0.16,1,0.3,1), height 0.28s cubic-bezier(0.16,1,0.3,1), transform 0.28s cubic-bezier(0.16,1,0.3,1)',
                }}
              />
            </svg>

            {caseStudies.map((study) => (
              <button
                key={study.id}
                ref={(el) => { tabRefs.current[study.id] = el; }}
                role="tab"
                aria-selected={activeId === study.id}
                className={`skills-toggle-btn${activeId === study.id ? ' skills-toggle-btn--active' : ''}`}
                onClick={() => setActiveId(study.id)}
              >
                {study.title}
              </button>
            ))}
          </div>
        </ScrollReveal>

        <AnimatePresence mode="wait">
          {activeStudy && (
            <motion.div
              key={activeStudy.id}
              className="case-studies"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18 }}
            >
              <CaseStudyCard study={activeStudy} index={activeIndex} />
            </motion.div>
          )}
        </AnimatePresence>

        <ScrollCue
          to="#skills"
          label="Scroll to skills"
          up="#about"
          upLabel="Scroll back to about"
        />
      </div>
    </div>
  );
};
